import CleaningStatus, { CLEANING_FILTER_STATUSES } from '@/dossier/constants/CleaningStatus';
import { WorkshopTeamId } from '@/general/models/WorkshopTeam';
import { BrandId } from '@/shared/models/Brand';
import CommonFilters from '@/shared/models/CommonFilters';
import { OutletId } from '@/shared/models/Outlet';
import { isPresent } from '@/shared/utils/typescript';
import { MemoCriteria } from '../client/MemoClient';
import { AssignedDossiersCriteria, CleaningCriteria, TaskCriteria } from '../client/TaskClient';
import TaskGrouping from '../constants/TaskGrouping';
import TaskStatus from '../constants/TaskStatus';
import CleaningTask from './CleaningTask';
import MemoTag, { MemoTagId } from './MemoTag';

export default class TaskFilters {
  status = TaskStatus.OPEN;
  grouping = TaskGrouping.BY_DOSSIER;
  memoTags: Array<MemoTag> = [];
  activeMemoTagIds: Array<MemoTagId> = [];
  interiorCleaningStatus: CleaningStatus | null = null;
  exteriorCleaningStatus: CleaningStatus | null = null;
  cleaningStatusToday = false;

  constructor(
    public common: CommonFilters,
  ) {
  }

  matches(outletId: OutletId, brandId: BrandId | null, teamId: WorkshopTeamId | null, status = this.status): boolean {
    return (this.common.activeOutletIds.length === 0 || this.common.activeOutletIds.includes(outletId))
      && (!isPresent(this.common.activeBrandId) || this.common.activeBrandId === brandId)
      && (!isPresent(this.common.activeTeamId) || this.common.activeTeamId === teamId)
      && this.status === status;
  }

  matchesCleaningTask(task: CleaningTask): boolean {
    if (!this.isIncludedCleaningStatus(task.exteriorCleaningStatus) && !this.isIncludedCleaningStatus(task.interiorCleaningStatus)) return false;
    return this.matchesCleaningStatus(task.exteriorCleaningStatus, this.exteriorCleaningStatus) && this.matchesCleaningStatus(task.interiorCleaningStatus, this.interiorCleaningStatus);
  }

  private matchesCleaningStatus(taskValue: CleaningStatus | null, filterValue: CleaningStatus | null): boolean {
    return filterValue === null || filterValue === taskValue;
  }

  private isIncludedCleaningStatus(taskValue: CleaningStatus | null): boolean {
    return taskValue !== null && CLEANING_FILTER_STATUSES.includes(taskValue);
  }

  getActiveMemoTags(): Array<MemoTag> {
    return this.memoTags.filter(t => this.activeMemoTagIds.includes(t.id));
  }

  getActiveCleaningStatus(interior: boolean): CleaningStatus | null {
    return interior ? this.interiorCleaningStatus : this.exteriorCleaningStatus;
  }

  asAssignedDossiersCriteria(): AssignedDossiersCriteria {
    return {
      outletIds: this.common.activeOutletIds,
      brandId: this.common.activeBrandId,
      teamId: this.common.activeTeamId,
    };
  }

  asTaskCriteria(): TaskCriteria {
    return {
      ...this.asAssignedDossiersCriteria(),
      status: this.status,
    };
  }

  asMemoCriteria(): MemoCriteria {
    return {
      ...this.asAssignedDossiersCriteria(),
      userIds: this.common.activeUserIds,
      tagIds: this.activeMemoTagIds,
    };
  }

  asCleaningCriteria(): CleaningCriteria {
    return {
      ...this.asAssignedDossiersCriteria(),
      interiorCleaningStatus: this.interiorCleaningStatus,
      exteriorCleaningStatus: this.exteriorCleaningStatus,
    };
  }
}
